/**
 * Opt-in testimonials — a continuous, auto-scrolling marquee with faded side edges for a
 * premium feel (CSS-only animation, no carousel library). Consent recorded server-side;
 * minors never shown; anonymised under campaign-safe mode. Pauses on hover/focus and is
 * fully disabled under prefers-reduced-motion (becomes a static, scrollable row).
 */
import { useEffect, useState } from 'react';
import { Quote } from 'lucide-react';
import { publicApi } from '../lib/publicApi.js';
import styles from './Testimonials.module.css';

export function Testimonials() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    publicApi.testimonials().then(setItems).catch(() => setItems([]));
  }, []);

  if (items && items.length === 0) return null;

  const loading = !items;
  const base = items || Array.from({ length: 4 }).map((_, i) => ({ id: `skel-${i}`, skeleton: true }));
  // Build one set that comfortably fills the viewport, then render it exactly twice. The
  // track translates by -50% — i.e. exactly one set width — so the loop is seamless for
  // any number of testimonials. The second set is decorative (hidden from a11y).
  const fillReps = Math.max(1, Math.ceil(6 / base.length));
  const oneSet = Array.from({ length: fillReps }).flatMap(() => base);
  const setLen = oneSet.length;
  const marquee = [...oneSet, ...oneSet];

  return (
    <section className={styles.section}>
      <div className="container">
        <p className="eyebrow">In their words</p>
        <h2 className={styles.title}>Opt-in, consent on record.</h2>
      </div>

      <div className={styles.viewport}>
        <div
          className={`${styles.track} ${loading ? styles.paused : ''}`}
          style={{ '--count': setLen }}
        >
          {marquee.map((t, i) => (
            <figure
              key={`${i}-${t.id}`}
              className={`${styles.card} ${t.skeleton ? styles.skel : ''}`}
              aria-hidden={i >= setLen ? 'true' : undefined}
            >
              {!t.skeleton && (
                <>
                  <Quote className={styles.quoteIcon} size={26} aria-hidden="true" />
                  <blockquote>{t.text}</blockquote>
                  <figcaption>
                    <strong>{t.author}</strong>
                    {t.village && <span>{t.village}</span>}
                  </figcaption>
                </>
              )}
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Testimonials;
